import { useEffect, useState } from 'react';
import headerLogo from '../../assets/차트트레이너.png';
import type { AuthUser } from '../../services/authService';
import { getMyPageData } from '../../services/mypageService';
import {
  getCachedMyRank,
  type UserRankWithBadge,
} from '../../services/userRankService';
import type { GameEndData } from '../../shared/types/game.types';
import { TitleBadge } from '../TitleBadge';
import { TrophyBadge } from '../TrophyBadge';

interface TopInfoBarProps {
  theme: 'light' | 'dark';
  toggleTheme: () => void;
  isAutoMode: boolean;
  isGameActive: boolean;
  timeLeft: number;
  formatTime: (seconds: number) => string;
  user: AuthUser | null;
  formatBalance: (value: number) => string;
  showRankingModal: boolean;
  onToggleRanking: () => void;
  showTradingHistoryModal: boolean;
  onToggleTradingHistory: () => void;
  showMyPageModal: boolean;
  onToggleMyPage: () => void;
  onLoginClick: () => void;
  onLogoutClick: () => void;
  onGoHome?: () => void; // 홈으로 가기 (게임 화면)
  apiBaseUrl: string;
  gameEndData?: GameEndData | null; // 게임 종료 데이터 (업적 획득 감지용)
}

export const TopInfoBar = ({
  theme,
  toggleTheme,
  isAutoMode,
  isGameActive,
  timeLeft,
  formatTime,
  user,
  formatBalance,
  showRankingModal,
  onToggleRanking,
  showTradingHistoryModal,
  onToggleTradingHistory,
  showMyPageModal,
  onToggleMyPage,
  onLoginClick,
  onLogoutClick,
  onGoHome,
  apiBaseUrl,
  gameEndData,
}: TopInfoBarProps) => {
  const [userRank, setUserRank] = useState<UserRankWithBadge | null>(null);
  const [selectedTitle, setSelectedTitle] = useState<{
    titleId: number;
    name: string;
    icon: string;
    rarity: 'common' | 'rare' | 'epic' | 'legendary';
  } | null>(null);
  // 사용자 랭크 및 칭호 조회
  useEffect(() => {
    const fetchUserData = async () => {
      if (user?.email) {
        try {
          // 잔고가 변경되었을 때는 캐시를 무시하고 새로 조회
          const forceRefresh = false; // 필요시 true로 변경하여 항상 최신 데이터 조회
          const rankData = await getCachedMyRank(apiBaseUrl, forceRefresh);
          setUserRank(rankData);

          // 마이페이지 데이터에서 선택한 칭호 조회
          const myPageData = await getMyPageData(apiBaseUrl);
          if (myPageData.selectedTitle) {
            setSelectedTitle({
              titleId: myPageData.selectedTitle.titleId,
              name: myPageData.selectedTitle.name,
              icon: myPageData.selectedTitle.icon,
              rarity:
                (myPageData.selectedTitle.rarity as
                  | 'common'
                  | 'rare'
                  | 'epic'
                  | 'legendary') || 'common',
            });
          } else {
            setSelectedTitle(null);
          }
        } catch (error) {
          console.error('사용자 데이터 조회 실패:', error);
          console.error(
            '에러 상세:',
            error instanceof Error ? error.message : String(error)
          );
          console.error(
            '에러 스택:',
            error instanceof Error ? error.stack : 'N/A'
          );
          setUserRank(null);
          setSelectedTitle(null);
        }
      } else {
        setUserRank(null);
        setSelectedTitle(null);
      }
    };

    fetchUserData();
  }, [
    user?.email,
    user?.balance,
    apiBaseUrl,
    showMyPageModal,
    // 게임 종료 후 업적 획득 시 칭호 업데이트
    gameEndData?.unlockedAchievements?.unlockedTitles?.length,
    gameEndData?.unlockedAchievements?.unlockedAchievements?.length,
  ]);

  return (
    <div className="top-infobar">
      <div className="infobar-card">
        <div
          className="header-brand"
          onClick={onGoHome}
          style={{ cursor: onGoHome ? 'pointer' : 'default' }}
        >
          <img
            src={headerLogo}
            alt="차트 트레이너 로고"
            className="header-logo"
          />
          <span className="header-brand-name">차트 트레이너</span>
          <span className="beta-badge">베타</span>
        </div>
        {isAutoMode && isGameActive && (
          <span className="timer">
            남은시간: <strong>{formatTime(timeLeft)}</strong>
          </span>
        )}

        <div className="header-right">
          <button onClick={toggleTheme} className="theme-toggle-btn">
            {theme === 'dark' ? '☀️' : '🌙'}
          </button>
          <button
            onClick={() => {
              onToggleRanking();
            }}
            className={`ranking-btn ${showRankingModal ? 'active' : ''}`}
          >
            🏆 랭킹 보기
          </button>

          {user && (
            <>
              <button
                onClick={() => {
                  onToggleTradingHistory();
                }}
                className={`trading-history-btn ${
                  showTradingHistoryModal ? 'active' : ''
                }`}
              >
                📊 매매내역
              </button>
              <button
                onClick={() => {
                  onToggleMyPage();
                }}
                className={`mypage-btn ${showMyPageModal ? 'active' : ''}`}
              >
                👤 마이페이지
              </button>
            </>
          )}

          {user ? (
            <div className="user-info">
              <span
                className="user-nickname"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                }}
              >
                {userRank && userRank.rank <= 10 && userRank.badge && (
                  <TrophyBadge
                    rank={userRank.rank}
                    size="small"
                    title={userRank.badge.name}
                  />
                )}
                {selectedTitle && (
                  <TitleBadge title={selectedTitle} size="small" />
                )}
                {user.nickname}
              </span>
              <span className="user-balance">
                {formatBalance(user.balance)}
              </span>
              <button onClick={onLogoutClick} className="logout-btn">
                로그아웃
              </button>
            </div>
          ) : (
            <button onClick={onLoginClick} className="login-btn">
              로그인
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
